/**
 * Офлайн НА ЧИТАННЯ: останній вдалий відповідь на кожен GET.
 *
 * Навіщо. Черга дій (`offlineQueue`) рятує лише ЗАПИС: швея може взяти пачку
 * без мережі, і дія піде пізніше. А от відкрити список своєї роботи без
 * звʼязку було неможливо взагалі — екран показував «немає зʼєднання» і
 * порожнечу. У цеху з поганим Wi-Fi це означає, що людина не бачить навіть
 * того, що вже тримає в руках.
 *
 * ГОЛОВНЕ ПРАВИЛО, і воно тут не косметичне. Дані з кешу ЗАВЖДИ позначені
 * часом: «станом на 14:32». Показник, який залежить від того, коли ти востаннє
 * мав звʼязок, і виглядає як свіжий, — це та сама брехня, що вже коштувала
 * грошей на лічильниках («Прострочено 9» проти 26). Тому кеш віддається лише
 * коли мережі СПРАВДІ немає, і завжди з міткою часу.
 *
 * Помилка сервера кешем НЕ підмінюється: 500 має лишитись 500, інакше поломка
 * знову сховається за старими даними.
 */
const DB_NAME = "mes-offline";
const STORE = "reads";
/** Версія 2: додає сховище `reads` поряд із наявним `queue`. */
const DB_VERSION = 2;

/** Скільки тримаємо. Старіше — радше шкода, ніж користь. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

interface CachedRead {
  url: string;
  data: unknown;
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // `queue` створює `offlineQueue`; тут лише додаємо своє сховище й НЕ
      // чіпаємо чуже — інакше підняття версії знищило б чергу дій.
      if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "url" });
    };
    // `blocked` — коли інша вкладка ще тримає СТАРУ версію бази. Без цього
    // обробника `open` не викликає ні `onsuccess`, ні `onerror`: обіцянка
    // просто ніколи не виконується, і `await` висить вічно. `try/catch` зависання
    // не ловить. Наслідок був би найгіршим із можливих: `postCritical` завис би
    // на `enqueue`, дія не пішла б і не стала б у чергу, а `flushQueue` уже
    // виставив `flushing = true` — тобто синхронізація в цій вкладці зупинилась
    // би вся й мовчки. До підняття версії всі вкладки були v1 і `blocked` не
    // траплявся; тепер, у день викотки, це реальний випадок.
    req.onblocked = () => reject(new Error("offline-db: інша вкладка тримає стару версію"));
    req.onsuccess = () => {
      // Стара вкладка мусить відпустити базу, коли нова піднімає версію —
      // інакше вона й буде тим, хто блокує.
      req.result.onversionchange = () => req.result.close();
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

/** Час останніх показаних застарілих даних (0 — таких немає). */
let staleAt = 0;
const listeners = new Set<(at: number) => void>();

export function subscribeStale(fn: (at: number) => void): () => void {
  listeners.add(fn);
  fn(staleAt);
  return () => listeners.delete(fn);
}

function markStale(at: number): void {
  staleAt = at;
  listeners.forEach((f) => f(at));
}

/** Мережа повернулась — мітка більше не потрібна. */
export function clearStale(): void {
  if (staleAt !== 0) markStale(0);
}

export async function saveRead(url: string, data: unknown): Promise<void> {
  try {
    await tx("readwrite", (s) => s.put({ url, data, savedAt: Date.now() } as CachedRead));
  } catch (e) {
    // Місце скінчилось — і це НЕ дрібниця: база в нас спільна з чергою дій,
    // тож переповнений кеш читань здатен не дати записати ВЗЯТУ ПАЧКУ. Кеш
    // тут другорядний, тому просто скидаємо його цілком і пробуємо ще раз.
    // Ліниво й надійно: LRU тут коштував би більше, ніж важить уся вигода.
    if ((e as DOMException | null)?.name === "QuotaExceededError") {
      try {
        await tx("readwrite", (s) => s.clear());
        await tx("readwrite", (s) => s.put({ url, data, savedAt: Date.now() } as CachedRead));
      } catch {
        // Не вийшло — переживемо: без кешу екран просто покаже помилку,
        // а це чесніше за старі дані.
      }
    }
    // Інші збої сховища робота цеху не помічає — і не має помічати.
  }
}

/**
 * Забути все. Викликається при ВИХОДІ.
 *
 * Ключ кешу — сама адреса, а на персональних роутах адреса в усіх однакова:
 * актора сервер бере з токена (`/sewing/my/earnings`, `/my/cycle`,
 * `/salary/…`, `/cash/…`). На спільному планшеті цеху це означало б, що
 * наступна людина офлайн побачить ЧУЖІ гроші — і повз перевірку прав, бо
 * запиту на сервер не буде взагалі. Той самий урок уже куплено на кеші фото
 * (`store/auth.ts::logout`), лише цей ще й переживав перезавантаження.
 */
export async function clearReads(): Promise<void> {
  clearStale();
  try {
    await tx("readwrite", (s) => s.clear());
  } catch {
    // Не змогли почистити — краще, ніж зламати вихід. Але й не мовчимо.
    // eslint-disable-next-line no-console
    console.warn("не вдалось очистити офлайн-кеш читань");
  }
}

/** Останній вдалий відповідь, якщо він ще не протух. */
export async function loadRead<T>(url: string): Promise<{ data: T; savedAt: number } | null> {
  try {
    const row = await tx<CachedRead | undefined>(
      "readonly",
      (s) => s.get(url) as IDBRequest<CachedRead | undefined>,
    );
    if (!row) return null;
    if (Date.now() - row.savedAt > MAX_AGE_MS) return null;
    markStale(row.savedAt);
    return { data: row.data as T, savedAt: row.savedAt };
  } catch {
    return null;
  }
}
